/**
 * Telling apart the three things that stop a wallet connection.
 *
 * Only one of them is a fault. A person who closed the wallet popup made a
 * choice, and a person whose earlier request is still waiting in the wallet
 * needs pointing at it — neither has hit an error, and showing them one is
 * both wrong and unhelpful: the reported case was a second click on MetaMask
 * that produced "Some errors occurred during connecting" when the honest
 * answer was "your wallet is already asking you — go and approve it".
 */

/** What a provider error looks like, whichever wrapper it arrives in. */
const codeOf = (error: unknown): number | undefined => {
  const e = error as { code?: unknown; cause?: { code?: unknown } } | null
  const raw = e?.code ?? e?.cause?.code
  const n = typeof raw === 'string' ? Number(raw) : raw
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined
}

const textOf = (error: unknown): string => {
  const e = error as { message?: unknown; cause?: { message?: unknown } } | null
  return `${String(e?.message ?? '')} ${String(e?.cause?.message ?? '')}`.toLowerCase()
}

/**
 * The wallet is already asking, from a request that has not been answered.
 *
 * EIP-1193 gives this `-32002`, and wallets word it variously — "already
 * processing eth_requestAccounts", "request already pending". Both are
 * matched, because a code alone has been known to arrive wrapped and lost.
 */
export const isConnectionAlreadyPending = (error: unknown): boolean => {
  if (codeOf(error) === -32002) return true
  const text = textOf(error)
  return (
    text.includes('already processing') ||
    text.includes('already pending') ||
    text.includes('request of type') ||
    text.includes('permissions request') ||
    text.includes('resource unavailable')
  )
}

/**
 * The WalletConnect pairing was never answered in time.
 *
 * The QR sits on screen for a couple of minutes and then the proposal dies.
 * Six of these in a week were answered with "Some errors occurred during
 * connecting", which reads like our fault and tells nobody to simply try
 * again with a fresh code.
 */
export const isConnectionProposalExpired = (error: unknown): boolean => {
  const text = textOf(error)
  return text.includes('proposal expired') || text.includes('pairing expired') || text.includes('request expired')
}

/**
 * The wallet extension was reloaded or updated while the page was open, so the
 * bridge into it is gone. Nothing is wrong with the page or the wallet; the
 * tab has to be refreshed.
 */
export const isExtensionContextInvalidated = (error: unknown): boolean =>
  textOf(error).includes('extension context invalidated')

/** The person closed the wallet popup. A decision, not a failure. */
export const isConnectionRejected = (error: unknown): boolean => {
  if (codeOf(error) === 4001) return true
  const text = textOf(error)
  return text.includes('user rejected') || text.includes('user denied') || text.includes('rejected the request')
}
